import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
interface SubLink {
  name: string;
  path: string;
}
interface NavItem {
  name: string;
  path?: string;
  children?: SubLink[];
}
const navItems: NavItem[] = [{
  name: 'Meet Milangela',
  path: '/meet-milangela'
}, {
  name: 'The Garden',
  children: [{
    name: 'Journal',
    path: '/journal'
  }, {
    name: 'Podcasts',
    path: '/podcasts'
  }, {
    name: 'Guided Meditations',
    path: '/guided-meditations'
  }]
}, {
  name: 'Start Your Journey',
  path: '/start-your-journey'
}];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);
  const isChildActive = (children?: SubLink[]) => children?.some(c => location.pathname === c.path) ?? false;
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-cream/95 backdrop-blur-sm shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-330" data-mp="1rdbamkmq8upl">
      {/* Dark gradient scrim for readability on light hero images */}
      {!isScrolled && <div aria-hidden="true" className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/45 via-black/20 to-transparent pointer-events-none" data-id="element-331" data-mp="1mml5n8i7qtvf" />}
      <div className="relative max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-332" data-mp="1cooyersecj2l">
        {/* Logo */}
        <Link to="/" className="flex items-center z-50" data-id="element-333" data-mp="yp5phwrrh3p8">
          <Logo variant={isScrolled ? 'dark' : 'white'} className={`transition-all duration-300 ${isScrolled ? 'h-10 md:h-12' : 'h-14 md:h-16'} w-auto`} data-id="element-334" data-mp="yqqj9k694im1" />
        </Link>

        {/* Desktop Nav */}
        <nav ref={dropdownRef} className="hidden lg:flex items-center space-x-7" data-id="element-335" data-mp="2y204khtzfvt6">
          {navItems.map(item => {
          if (item.children) {
            const active = isChildActive(item.children);
            const isOpen = openDropdown === item.name;
            return <div key={item.name} className="relative" data-id="element-336" data-mp="w4psttqpwrza">
                  <button onClick={() => setOpenDropdown(isOpen ? null : item.name)} className={`inline-flex items-center gap-1 text-sm tracking-widest font-medium uppercase transition-colors ${active ? isScrolled ? 'text-sage-700' : 'text-sage-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]' : isScrolled ? 'text-charcoal hover:text-sage-600' : 'text-white hover:text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]'}`} data-id="element-337" data-mp="w6ar9ug5kv2f">
                    {item.name}
                    <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-338" data-mp="3gup1k5fl752y" />
                  </button>
                  {isOpen && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[220px] bg-white rounded-xl shadow-lg border border-cream-200 py-2 z-50" data-id="element-339" data-mp="230rpo53erwf3">
                      {item.children.map(child => <NavLink key={child.path} to={child.path} className={({
                  isActive
                }) => `block px-5 py-2.5 text-sm tracking-widest uppercase transition-colors ${isActive ? 'text-sage-700 bg-cream-50' : 'text-charcoal hover:bg-cream-50'}`} data-id="element-340" data-mp="2gyg8z8htuvi1">
                          {child.name}
                        </NavLink>)}
                    </div>}
                </div>;
          }
          return <NavLink key={item.name} to={item.path!} className={({
            isActive
          }) => `text-sm tracking-widest font-medium uppercase transition-colors ${isActive ? isScrolled ? 'text-sage-700' : 'text-sage-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]' : isScrolled ? 'text-charcoal hover:text-sage-600' : 'text-white hover:text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]'}`} data-id="element-341" data-mp="15irmsjcee0ia">
                {item.name}
              </NavLink>;
        })}
        </nav>

        {/* Actions */}
        <div className="hidden lg:flex items-center space-x-6" data-id="element-342" data-mp="aafz9s8gur3h">
          <LanguageSwitcher isScrolled={isScrolled} data-id="element-343" data-mp="a8v0fqf4clzt" />
          <button aria-label="Search" className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-344" data-mp="5abh7xrwtob5">
            <Search size={20} data-id="element-345" data-mp="1b2p0xcipyhp" />
          </button>
          <Link to="/lets-connect" className={`px-6 py-2 border text-sm tracking-widest transition-colors ${isScrolled ? 'border-charcoal text-charcoal hover:bg-charcoal hover:text-cream' : 'border-white text-white hover:bg-white hover:text-charcoal'}`} data-id="element-346" data-mp="3sfj6pjlwnf72">
            LET'S CONNECT
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button aria-label="Toggle menu" className="lg:hidden z-50" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-347" data-mp="3ipu7fc1tnajv">
          {mobileMenuOpen ? <X size={28} className="text-charcoal" data-id="element-348" data-mp="38vu2pbagc7bo" /> : <Menu size={28} className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-349" data-mp="epjz12bpw5m" />}
        </button>

        {/* Mobile Nav */}
        {mobileMenuOpen && <div className="fixed inset-0 bg-cream z-40 overflow-y-auto pt-24 pb-12 px-8" data-id="element-350" data-mp="3mtidfqat1y30">
            <nav className="flex flex-col space-y-5 max-w-md mx-auto" data-id="element-351" data-mp="3rrag9fczn4lz">
              {navItems.map(item => {
            if (item.children) {
              return <div key={item.name} data-id="element-352" data-mp="o4tiwv4kh7qa">
                      <p className="text-sm tracking-widest font-medium uppercase text-charcoal mb-3" data-id="element-353" data-mp="o5mlrl0end35">
                        {item.name}
                      </p>
                      <div className="pl-4 flex flex-col space-y-3 border-l border-cream-200" data-id="element-354" data-mp="23uawjxmrrleu">
                        {item.children.map(child => <NavLink key={child.path} to={child.path} className={({
                    isActive
                  }) => `text-base tracking-widest uppercase ${isActive ? 'text-sage-700' : 'text-charcoal-50'}`} data-id="element-355" data-mp="2dl5onimp5f87">
                            {child.name}
                          </NavLink>)}
                      </div>
                    </div>;
            }
            return <NavLink key={item.name} to={item.path!} className={({
              isActive
            }) => `text-lg tracking-widest font-medium uppercase ${isActive ? 'text-sage-700' : 'text-charcoal'}`} data-id="element-356" data-mp="3ad95063v9wwc">
                    {item.name}
                  </NavLink>;
          })}
              <div className="mt-6 flex flex-col items-start gap-6 border-t border-cream-200 pt-6" data-id="element-357" data-mp="1dj8wwhqkwflr">
                <LanguageSwitcher mobile data-id="element-358" data-mp="1idmxcyk3lcxa" />
                <Link to="/lets-connect" className="inline-block w-fit px-8 py-3 border border-charcoal text-charcoal text-base tracking-widest" data-id="element-359" data-mp="1me6s5vh784g9">
                  LET'S CONNECT
                </Link>
              </div>
            </nav>
          </div>}
      </div>
    </header>;
}